import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

interface SettingsScreenProps {
  onBack?: () => void;
}

type SettingsTab = 'appearance' | 'notifications' | 'data' | 'gitlab';

interface GitLabProject {
  id: number;
  name: string;
  path: string;
  description: string;
}

interface GitLabVersionResponse {
  version: string;
  revision: string;
}

interface GitLabSettings {
  apiUrl: string;
  token: string;
  defaultProject: string;
  availableProjects: GitLabProject[];
}

interface Project {
  id: string;
  name: string;
  description: string;
  csvFiles: Array<{
    id: string;
    name: string;
    originalName: string;
  }>;
  testIds: string[];
  gitlab: GitLabSettings | null;
  createdAt: string;
  createdBy: string;
}

const SettingsScreen: React.FC<SettingsScreenProps> = ({ onBack }) => {
  const { showToast } = useToast();
  const { theme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<SettingsTab>('appearance');
  const [gitlabConfig, setGitlabConfig] = useState<GitLabSettings>({
    apiUrl: '',
    token: '',
    defaultProject: '',
    availableProjects: []
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get<Project[]>('/api/projects', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setProjects(response.data);
      } catch {
        // ignore load error
      }
    };

    loadProjects();
  }, []);

  const handleProjectSelect = (projectId: string) => {
    setSelectedProject(projectId);
    const project = projects.find(p => p.id === projectId);
    if (project?.gitlab) {
      setGitlabConfig(project.gitlab);
    } else {
      setGitlabConfig({
        apiUrl: '',
        token: '',
        defaultProject: '',
        availableProjects: []
      });
    }
  };

  const handleTestGitLabConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!gitlabConfig.apiUrl || !gitlabConfig.token) {
        showToast('Wprowadź URL i token GitLab', 'error');
        return;
      }

      // Validate GitLab URL
      let apiUrl = gitlabConfig.apiUrl.trim();
      if (!apiUrl.startsWith('https://') && !apiUrl.startsWith('http://')) {
        showToast('Nieprawidłowy format URL GitLab', 'error');
        return;
      }

      // Remove trailing slashes and /api/v4 if present
      apiUrl = apiUrl.replace(/\/+$/, '').replace(/\/api\/v4$/, '');

      // Test connection
      const versionResponse = await axios.get<GitLabVersionResponse>('/api/settings/gitlab/version', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          apiUrl,
          gitlabToken: gitlabConfig.token
        }
      });

      // Get available projects
      const projectsResponse = await axios.get<GitLabProject[]>('/api/settings/gitlab/projects', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          apiUrl,
          gitlabToken: gitlabConfig.token
        }
      });

      const projects = projectsResponse.data.map(project => ({
        id: project.id,
        name: project.name,
        path: project.path,
        description: project.description
      }));

      setGitlabConfig(prev => ({
        ...prev,
        availableProjects: projects
      }));

      showToast('Połączenie z GitLab udane', 'success');
    } catch (error: any) {
      let errorMessage = 'Nie udało się połączyć z GitLab';
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Nieprawidłowy token GitLab';
        } else if (error.response.status === 404) {
          errorMessage = 'Nieprawidłowy URL GitLab';
        } else if (error.response.data?.details) {
          errorMessage = `Błąd GitLab: ${error.response.data.details}`;
        }
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleSaveGitLabConfig = async () => {
    try {
      if (!selectedProject) {
        alert('Wybierz projekt przed zapisaniem ustawień GitLab');
        return;
      }

      const token = localStorage.getItem('token');
      // Aktualizuj projekt z nową konfiguracją GitLab
      await axios.put(`/api/projects/${selectedProject}`, {
        gitlab: gitlabConfig
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Odśwież listę projektów
      const response = await axios.get<Project[]>('/api/projects', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setProjects(response.data);

      alert('Ustawienia GitLab zostały zapisane dla projektu.');
    } catch (error) {
      alert('Nie udało się zapisać ustawień GitLab. Spróbuj ponownie.');
    }
  };

  const renderAppearance = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Wygląd aplikacji</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Motyw
          </label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'system')}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="light">Jasny</option>
            <option value="dark">Ciemny</option>
            <option value="system">Zgodny z systemem</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rozmiar czcionki
          </label>
          <select className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
            <option value="small">Mały</option>
            <option value="medium">Średni</option>
            <option value="large">Duży</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Powiadomienia</h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Powiadomienia o nowych testach</h3>
            <p className="text-sm text-gray-500">Otrzymuj powiadomienia gdy pojawią się nowe testy</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Powiadomienia o wynikach</h3>
            <p className="text-sm text-gray-500">Otrzymuj powiadomienia gdy testy zostaną zakończone</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderData = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Zarządzanie danymi</h2>
      <div className="space-y-6">
        <div>
          <h3 className="font-medium text-gray-900 mb-2">Eksport danych</h3>
          <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            Eksportuj wszystkie dane
          </button>
        </div>
        <div>
          <h3 className="font-medium text-gray-900 mb-2">Import danych</h3>
          <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            Importuj dane
          </button>
        </div>
        <div>
          <h3 className="font-medium text-gray-900 mb-2 text-red-600">Niebezpieczna strefa</h3>
          <button className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
            Wyczyść wszystkie dane
          </button>
        </div>
      </div>
    </div>
  );

  const renderGitLabSettings = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Konfiguracja GitLab</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Wybierz projekt
          </label>
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedProject}
            onChange={(e) => handleProjectSelect(e.target.value)}
          >
            <option value="">Wybierz projekt</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        {selectedProject && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL API GitLab
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://gitlab.com/api/v4"
                value={gitlabConfig.apiUrl}
                onChange={(e) => setGitlabConfig(prev => ({ ...prev, apiUrl: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token dostępu
              </label>
              <input
                type="password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Wprowadź token dostępu GitLab"
                value={gitlabConfig.token}
                onChange={(e) => setGitlabConfig(prev => ({ ...prev, token: e.target.value }))}
              />
              <p className="mt-1 text-sm text-gray-500">
                Token można wygenerować w ustawieniach GitLab: Settings → Access Tokens
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domyślny projekt GitLab
              </label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={gitlabConfig.defaultProject}
                onChange={(e) => setGitlabConfig(prev => ({ ...prev, defaultProject: e.target.value }))}
              >
                <option value="">Wybierz projekt</option>
                {gitlabConfig.availableProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.path})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dostępne projekty GitLab
              </label>
              <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg">
                {gitlabConfig.availableProjects.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {gitlabConfig.availableProjects.map(project => (
                      <div key={project.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">{project.name}</h3>
                            <p className="text-sm text-gray-500">{project.path}</p>
                            {project.description && (
                              <p className="mt-1 text-sm text-gray-600">{project.description}</p>
                            )}
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={gitlabConfig.defaultProject === project.id.toString()}
                              onChange={() => setGitlabConfig(prev => ({
                                ...prev,
                                defaultProject: project.id.toString()
                              }))}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    Brak dostępnych projektów. Kliknij "Testuj połączenie" aby pobrać listę projektów.
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleTestGitLabConnection}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Testuj połączenie
              </button>
              <button
                onClick={handleSaveGitLabConfig}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Zapisz ustawienia
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'appearance':
        return renderAppearance();
      case 'notifications':
        return renderNotifications();
      case 'data':
        return renderData();
      case 'gitlab':
        return renderGitLabSettings();
      default:
        return null;
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-12 pt-24">
      <div className="w-full max-w-[2000px] mx-auto bg-white/60 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-4 p-2 rounded-lg bg-white dark:bg-slate-700 shadow-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
              >
                <svg
                  className="w-6 h-6 text-gray-600 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
            )}
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Ustawienia</h1>
          </div>
        </div>

        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => setActiveTab('appearance')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'appearance'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
          >
            Wygląd
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'notifications'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
          >
            Powiadomienia
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'data'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Dane
          </button>
          <button
            onClick={() => setActiveTab('gitlab')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'gitlab'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              GitLab
            </span>
          </button>
        </div>

        <div className="flex gap-8">
          {/* Menu boczne */}
          <div className="w-64 flex-shrink-0">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('appearance')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'appearance'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <svg
                  className="w-5 h-5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                  />
                </svg>
                Wygląd
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'notifications'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <svg
                  className="w-5 h-5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                Powiadomienia
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'data'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <svg
                  className="w-5 h-5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                  />
                </svg>
                Dane
              </button>
              <button
                onClick={() => setActiveTab('gitlab')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'gitlab'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <svg
                  className="w-5 h-5 mr-3"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                GitLab
              </button>
            </nav>
          </div>

          {/* Zawartość */}
          <div className="flex-1">
            {renderContent()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SettingsScreen; 